<template>
  <div class="map-mobile-wrapper">
    <!-- Simple Native Header -->
    <div class="map-nav-header">
       <a href="javascript:void(0);" @click="$router.push('/dashboard')" class="btn-back-mob shadow-sm">
          <vue-feather type="arrow-left" size="20"></vue-feather>
       </a>
       <h6 class="mb-0 fw-bold">Map Lokasi Kios</h6>
       <div style="width: 24px;"></div> <!-- Spacer for centering -->
    </div>

    <!-- Map Container -->
    <div id="mobile-fullscreen-map">
        <l-map 
          ref="mapRef" 
          v-if="mapCenter"
          :zoom="mapZoom" 
          :center="mapCenter" 
          :bounds="mapBounds"
          :options="{ zoomControl: false }"
          style="height: 100%; width: 100%;"
        >
          <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"></l-tile-layer>
          
          <l-marker 
             v-for="kios in kiosWithCoords" 
             :key="kios.id" 
             :lat-lng="{lat: parseFloat(kios.latitude), lng: parseFloat(kios.longitude)}"
          >
            <l-icon 
              :icon-size="[40, 40]"
              :icon-anchor="[20, 20]"
              class-name="custom-kios-marker-mob"
            >
              <div class="marker-pin-mob shadow-premium">
                <i class="fa fa-store"></i>
              </div>
            </l-icon>
            
            <l-popup :options="{ maxWidth: 300, className: 'mob-kios-popup' }">
               <div class="mob-popup-card">
                  <div v-if="kios.foto_toko" class="mob-popup-img">
                     <img :src="getImageUrl(kios.foto_toko)" alt="Store">
                  </div>
                  <div class="mob-popup-body p-3 text-center">
                     <h5 class="fw-bold mb-1 text-dark">{{ kios.nama_toko }}</h5>
                     <p class="smallest text-muted fw-bold uppercase mb-2">{{ kios.nama_member }}</p>
                     
                     <div class="mob-popup-address border-top pt-2 mb-3">
                        <p class="smallest text-muted mb-0 lh-base">{{ kios.alamat || 'Alamat tidak tersedia' }}</p>
                     </div>
                     
                     <div class="d-grid">
                        <router-link :to="'/people/kios-details/' + kios.id" class="btn btn-success rounded-pill fw-bold py-2">
                           Detail Toko
                        </router-link>
                     </div>
                  </div>
               </div>
            </l-popup>
          </l-marker>
        </l-map>
        
        <!-- Floating Zoom Controls (Minimalist) -->
        <div class="zoom-controls-mob">
           <button @click="zoomIn" class="zoom-btn mb-2"><vue-feather type="plus" size="18"></vue-feather></button>
           <button @click="zoomOut" class="zoom-btn"><vue-feather type="minus" size="18"></vue-feather></button>
        </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useDataStore } from '@/store/dataStore';
import { LMap, LTileLayer, LMarker, LIcon, LPopup } from '@vue-leaflet/vue-leaflet';
import 'leaflet/dist/leaflet.css';

export default {
  name: 'MapMobile',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup
  },
  setup() {
    const { members, loadAllData } = useDataStore();
    const mapRef = ref(null);
    const mapZoom = ref(11);
    const mapCenter = ref([-7.4313, 109.2478]);
    const apiBaseUrl = process.env.VUE_APP_API_URL || '';

    const kiosWithCoords = computed(() => {
        return (members.value || []).filter(m => {
            const lat = parseFloat(m.latitude);
            const lng = parseFloat(m.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        });
    });

    const mapBounds = computed(() => {
        if (kiosWithCoords.value.length === 0) return null;
        return kiosWithCoords.value.map(k => [parseFloat(k.latitude), parseFloat(k.longitude)]);
    });

    const getImageUrl = (path) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return `${apiBaseUrl}${path.startsWith('/') ? path : '/' + path}`;
    };

    const zoomIn = () => { if(mapRef.value) mapRef.value.leafletObject.zoomIn(); };
    const zoomOut = () => { if(mapRef.value) mapRef.value.leafletObject.zoomOut(); };

    onMounted(async () => {
      if (members.value.length === 0) await loadAllData();
      
      setTimeout(() => {
          if (mapRef.value && mapBounds.value) {
              mapRef.value.leafletObject.fitBounds(mapBounds.value, { padding: [50, 50] });
          }
      }, 500);
    });

    return {
      mapRef,
      mapZoom,
      mapCenter,
      kiosWithCoords,
      mapBounds,
      getImageUrl,
      zoomIn,
      zoomOut
    };
  }
};
</script>

<style scoped>
.map-mobile-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2000;
  background: white;
  display: flex;
  flex-direction: column;
}

.map-nav-header {
  height: 60px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-bottom: 1px solid #f1f5f9;
  z-index: 2001;
}

.btn-back-mob {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e293b;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
}
.btn-back-mob:active {
  transform: scale(0.9);
  background: #f8fafc;
}

#mobile-fullscreen-map {
  flex: 1;
  position: relative;
}

/* Custom Marker */
:deep(.custom-kios-marker-mob) {
  background: transparent;
  border: none;
}

.marker-pin-mob {
  width: 40px;
  height: 40px;
  background-color: #029147;
  border-radius: 50% 50% 50% 0; /* Dropshape */
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 3px solid #ffffff;
  transform: rotate(-45deg);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.marker-pin-mob i {
  transform: rotate(45deg);
  font-size: 16px;
}

.shadow-premium {
  box-shadow: 0 4px 12px rgba(2, 145, 71, 0.4);
}

/* Popup Styles */
:deep(.mob-kios-popup) .leaflet-popup-content-wrapper {
  padding: 0;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
}

:deep(.mob-kios-popup) .leaflet-popup-content {
  margin: 0;
  width: 260px !important;
}

.mob-popup-img {
  height: 140px;
  width: 100%;
  overflow: hidden;
}

.mob-popup-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mob-popup-address {
    padding: 0 16px;
}

.mob-popup-address p {
    font-size: 11px;
    color: #64748b;
    line-height: 1.5;
}

.uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
.smallest { font-size: 11px; }

/* Zoom Controls */
.zoom-controls-mob {
  position: absolute;
  top: 20px;
  right: 16px;
  z-index: 2005;
  display: flex;
  flex-direction: column;
}

.zoom-btn {
  width: 44px;
  height: 44px;
  background: white;
  border-radius: 14px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #029147;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  font-weight: bold;
}

/* Button style override */
.btn-success {
    background-color: #029147;
    border-color: #029147;
}
</style>
